import { request } from "../helpers/axios.new";

export const GET_GROUP_KPIS = (companyId, groupId = null, includeRetired = false) => {
  const params = [
    ...(groupId ? [`groupId=${groupId}`] : []),
    ...(includeRetired ? ["includeRetired=1"] : []),
  ];
  return request({
    url: `/kpi/${companyId}${params.length ? `?${params.join("&")}` : ""}`,
  });
};

export const POST_GROUP_KPIS = (companyId, data) =>
  request({
    url: `/kpi/${companyId}`,
    method: "POST",
    data,
  });

export const CREATE_CUSTOM_KPI = (data) =>
  request({
    url: `/kpi/custom`,
    method: "PUT",
    data,
    // Without this, a stalled connection leaves the create-KPI dialog spinning
    // forever — axios has no default timeout, so a hung request never resolves
    // or rejects on its own.
    timeout: 15000,
  });

// Previously entered values for one of a metric's detail inputs (e.g. the
// Memberships plan name) — feeds the manual-entry dropdown suggestions.
export const GET_KPI_DETAIL_VALUES = (kpiId, field) =>
  request({
    url: `/kpi/detail-values/${kpiId}?field=${encodeURIComponent(field)}`,
  });

export const GET_ALL_USER_KPIS = (companyId, groupId = null, month = null, options = {}) => {
  const query = [
    ...(groupId ? [`groupId=${groupId}`]: []),
    ...(month ? [`month=${month}`]: []),
    ...(options.bustCache ? [`_=${Date.now()}`] : []),
  ];
  return request({
    url: `/ekpi/${companyId}?${query.join("&")}`,
    headers: options.bustCache
      ? { "Cache-Control": "no-cache", Pragma: "no-cache" }
      : undefined,
  });
}

/** Per day: what was scheduled and how much of it is completed (Total
 *  Scheduled Revenue's two-tone bars). from / to are YYYY-MM-DD, inclusive. */
export const GET_SCHEDULED_REVENUE_DAILY = (from, to) => request({
  url: `/ekpi/scheduled-revenue/daily?from=${from}&to=${to}`,
});

export const GET_ALL_COMPANY_SCORES = (daily = null, month = null, period = null) => {
  const params = [];
  if (daily) params.push("daily=1");
  if (month) params.push(`month=${month}`);
  if (period) params.push(`period=${period}`);
  const query = params.length ? `?${params.join("&")}` : "";
  return request({
    url: `/ekpi/company${query}`,
  });
}

export const ADD_USER_KPI = ({kpiId, companyId, userId, month, entryId, data}) => {
 
  const query = [
    ...(kpiId ? [`kpiId=${kpiId}`]: []),
    ...(userId ? [`userId=${userId}`]: []),
    ...(companyId ? [`companyId=${companyId}`]: []),
    ...(month ? [`month=${month}`]: []),
    ...(entryId ? [`entryId=${entryId}`]: []),
  ];

  return request({
    method: "POST",
    url: `/ekpi/?${query.join("&")}`,
    data,
  });
}

export const UPDATE_USER_KPI = (id, kpiId, amount, ownerId) =>
  request({
    method: "PUT",
    url: `/ekpi/${id}`,
    data: { kpiId, amount, ownerId },
  });

// Admin-only review table: paginated Bid Quality form submissions across
// every Sales workspace (server discovers the form KPIs itself).
export const GET_BID_QUALITY_SUBMISSIONS = ({ dateFrom, dateTo, search, page = 1, limit = 50 } = {}) => {
  const query = [
    ...(dateFrom ? [`dateFrom=${encodeURIComponent(dateFrom)}`] : []),
    ...(dateTo ? [`dateTo=${encodeURIComponent(dateTo)}`] : []),
    ...(search ? [`search=${encodeURIComponent(search)}`] : []),
    `page=${page}`,
    `limit=${limit}`,
  ];
  return request({ url: `/ekpi/bid-quality/submissions?${query.join("&")}` });
};

// Where (if anywhere) to send an email when a technician submits a Job Bid
// Quality score. The server emails these recipients on every submission.
export const GET_BID_QUALITY_NOTIFICATION_SETTINGS = () =>
  request({ url: `/ekpi/bid-quality/notification-settings` });

export const UPDATE_BID_QUALITY_NOTIFICATION_SETTINGS = ({ enabled, recipientEmails }) =>
  request({
    method: "PUT",
    url: `/ekpi/bid-quality/notification-settings`,
    data: { enabled, recipientEmails },
  });

export const UPDATE_EKPI_IMAGE_ROTATION = (id, imageRotation) =>
  request({
    method: "PATCH",
    url: `/ekpi/${id}/image-rotation`,
    data: { imageRotation },
  });

export const UPDATE_TOTAL_SCORE = (kpiId, userId, amount, month = null) =>
  request({
    method: "PUT",
    url: `/ekpi/total/${kpiId}`,
    data: {
      userId,
      amount,
      ...(month ? { month } : {}),
    },
  });

export const DELETE_USER_KPI = (kpiId) =>
  request({
    method: "DELETE",
    url: `/ekpi/${kpiId}`,
  });


export const GET_USER_KPIS = (companyId, userId = null, kpiId = null, options = {}) => {
  const query = [
    ...(userId ? [`userId=${userId}`]: []),
    ...(kpiId ? [`kpiId=${kpiId}`]: []),
    ...(options.days != null ? [`days=${options.days}`] : []),
    ...(options.startDate ? [`startDate=${options.startDate}`] : []),
    ...(options.endDate ? [`endDate=${options.endDate}`] : []),
    ...(options.limit != null ? [`limit=${options.limit}`] : []),
    ...(options.before ? [`before=${encodeURIComponent(options.before)}`] : []),
  ];

  // either userId or kpiId can be empty, but server rejects request if both are
  if (query.length) {
    return request({
      method: "GET",
      url: `/ekpi/users/${companyId}?${query.join("&")}`,
    });
  }
  return Promise.reject(new Error("Empty query parameters"));
};

export const UPDATE_GROUP_KPI = (groupId, kpiObj) =>
  request({
    method: "PUT",
    url: `/kpi/${groupId}`,
    data: kpiObj,
  });

export const UPDATE_KPI_GUIDE_TEXT = (kpiId, guideText) =>
  request({
    method: "PATCH",
    url: `/kpi/guide-text/${kpiId}`,
    data: { guideText },
  });

export const UPDATE_KPI_DAILY_GOAL_TEXT = (kpiId, dailyGoalText) =>
  request({
    method: "PATCH",
    url: `/kpi/daily-goal-text/${kpiId}`,
    data: { dailyGoalText },
  });

  export const DISABLE_GROUP_KPI = (kpiId) =>
  request({
    method: "DELETE",
    url: `/kpi/${kpiId}`,
  });

/** On-demand Closed Rate + Bid Accuracy for a date window. */
export const POST_WINDOW_RATES = ({ groupId, from, to, kpiIds } = {}) =>
  request({
    method: "POST",
    url: `/kpi/window-rates`,
    data: {
      ...(groupId ? { groupId } : {}),
      from,
      to,
      ...(Array.isArray(kpiIds) && kpiIds.length ? { kpiIds } : {}),
    },
  });

  export const SET_GROUP_KPI_ACCESS = (workspaceId, params) => request({
    method: "PUT",
    url: `kpi/access/${workspaceId}`,
    data: params
  })